/**
 * SessionBillingPopover
 *
 * Unified billing/session/attestation editor used across Encounter View,
 * Note View, and sign flow. A single canonical popover that replaces
 * AttestationsPopover, AttestationsBox, and scattered billing editors.
 *
 * Sections are collapsible accordions. The `focusSection` prop auto-expands
 * the targeted section when the popover opens.
 *
 * Controlled component — receives current values, fires callbacks.
 * Zero internal data fetching. Parents decide where data comes from.
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Check as CheckIcon,
  ExpandMore as ExpandMoreIcon,
} from '@mui/icons-material';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Popover,
  Typography,
} from '@mui/material';
import { useEventTracking } from '@services/statsig';
import type { WorkingBillingClaimLineItemResponse4 } from '@store/generated/encounters';

import type { EncounterAttestations, Modality } from '../../types/attestations';

import { AttestationsSection } from './AttestationsSection';
import { BillingCodesSection } from './BillingCodesSection';
import { SessionDetailsSection } from './SessionDetailsSection';
import type { PopoverSection, SessionBillingPopoverProps } from './types';

const SECTION_ORDER: PopoverSection[] = ['session', 'billing', 'attestations'];

const SECTION_LABELS: Record<PopoverSection, string> = {
  session: 'Session Details',
  billing: 'Billing Codes',
  attestations: 'Attestations',
};

const MODALITY_LABELS: Record<Modality, string> = {
  video: 'Video',
  audio: 'Audio',
  in_person: 'In-person',
};

function getSessionSummary(modality: Modality | null, duration: number | null): string | null {
  const parts: string[] = [];
  if (modality) parts.push(MODALITY_LABELS[modality]);
  if (duration) parts.push(`${duration} min`);
  return parts.length > 0 ? parts.join(' · ') : null;
}

function getBillingSummary(cptCodes: string[], icd10Codes: string[]): string | null {
  const parts: string[] = [];
  if (cptCodes.length > 0) parts.push(cptCodes.join(', '));
  if (icd10Codes.length > 0) parts.push(icd10Codes.join(', '));
  return parts.length > 0 ? parts.join(' · ') : null;
}

function getAttestationsSummary(
  attestations: Partial<EncounterAttestations>,
  modality: Modality | null,
  hasPatient: boolean,
): string | null {
  const isTelehealth = modality === 'video' || modality === 'audio';
  const visible: boolean[] = [];
  if (isTelehealth) visible.push(!!attestations.telehealth_consent);
  if (isTelehealth) visible.push(!!attestations.location_confirmed);
  if (hasPatient) visible.push(!!attestations.pdmp_reviewed);
  if (visible.length === 0) return null;
  const checked = visible.filter(Boolean).length;
  return checked === visible.length ? 'All complete' : `${checked} of ${visible.length}`;
}

export const SessionBillingPopover: React.FC<SessionBillingPopoverProps> = ({
  open,
  anchorEl,
  onClose,
  focusSection,

  modality,
  onModalityChange,
  placeOfService,
  onPlaceOfServiceChange,
  duration,
  onDurationChange,

  startTime,
  endTime,
  encounterDate,
  dateOfService,
  onTimeChange,

  cptCodes,
  onCptCodesChange,
  cptLineItems,
  onCptLineItemsChange,
  suggestedCptCodes,
  onUseSuggestedCptCodes,
  icd10Codes,
  onIcd10CodesChange,

  attestations,
  onAttestationChange,

  hasPatient = false,
  editable = true,

  showSignButton = false,
  onSignNote,
  isSigning = false,
  hasNoteSigning = false,
  needsCosign = false,
}) => {
  const { encounter: encounterTracking } = useEventTracking();

  // Section refs for scroll-to behavior
  const sectionRefs = useRef<Record<PopoverSection, HTMLDivElement | null>>({
    session: null,
    billing: null,
    attestations: null,
  });

  // Track which single accordion panel is expanded (only one at a time)
  const [expanded, setExpanded] = useState<PopoverSection | null>(null);

  // When popover opens, expand the focused section (collapse others)
  useEffect(() => {
    if (open) {
      setExpanded(focusSection ?? 'session');
      encounterTracking.billingPopoverOpened({ focus_section: focusSection ?? 'session' });
    }
  }, [open, focusSection, encounterTracking]);

  // Scroll to focused section after accordion expansion
  useEffect(() => {
    if (!open || !focusSection) return;

    const timer = setTimeout(() => {
      const el = sectionRefs.current[focusSection];
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 200); // Wait for accordion transition

    return () => clearTimeout(timer);
  }, [open, focusSection]);

  const handleAccordionChange = useCallback(
    (section: PopoverSection) =>
      (_: React.SyntheticEvent, isExpanded: boolean) => {
        setExpanded(isExpanded ? section : null);
        encounterTracking.billingPopoverSectionToggled({ section, expanded: isExpanded });
      },
    [encounterTracking],
  );

  // Check if attestations section has visible items
  const isTelehealth = modality === 'video' || modality === 'audio';
  const hasAttestationItems = isTelehealth || hasPatient;

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      slotProps={{
        paper: {
          sx: {
            width: 460,
            maxHeight: '80vh',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
          },
        },
      }}
    >
      {/* Scrollable accordion content */}
      <Box
        sx={{
          flex: 1,
          overflowY: 'auto',
          overflowX: 'hidden',
        }}
      >
        {SECTION_ORDER.map(section => {
          // Hide attestations accordion if no visible items
          if (section === 'attestations' && !hasAttestationItems) return null;

          const isExpanded = expanded === section;
          const summary =
            section === 'session'
              ? getSessionSummary(modality, duration)
              : section === 'billing'
                ? getBillingSummary(cptCodes, icd10Codes)
                : getAttestationsSummary(attestations, modality, hasPatient);

          return (
            <Accordion
              key={section}
              expanded={isExpanded}
              onChange={handleAccordionChange(section)}
              disableGutters
              elevation={0}
              square
              ref={(el: HTMLDivElement | null) => {
                sectionRefs.current[section] = el;
              }}
              sx={{
                '&:before': { display: 'none' },
                borderBottom: 1,
                borderColor: 'divider',
                '&:last-of-type': { borderBottom: 0 },
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                sx={{
                  minHeight: 44,
                  '& .MuiAccordionSummary-content': {
                    my: 0.5,
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 1,
                    overflow: 'hidden',
                  },
                }}
              >
                <Typography
                  variant="overline"
                  sx={{
                    fontWeight: 600,
                    letterSpacing: 1,
                    color: 'text.secondary',
                    flexShrink: 0,
                  }}
                >
                  {SECTION_LABELS[section]}
                </Typography>
                {!isExpanded && summary && (
                  <Typography
                    variant="caption"
                    sx={{
                      color: 'text.disabled',
                      fontSize: '0.75rem',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      minWidth: 0,
                    }}
                  >
                    {summary}
                  </Typography>
                )}
              </AccordionSummary>
              <AccordionDetails sx={{ pt: 0, pb: 2, px: 2 }}>
                {section === 'session' && (
                  <SessionDetailsSection
                    modality={modality}
                    onModalityChange={onModalityChange}
                    placeOfService={placeOfService}
                    onPlaceOfServiceChange={onPlaceOfServiceChange}
                    duration={duration}
                    onDurationChange={onDurationChange}
                    editable={editable}
                    startTime={startTime}
                    endTime={endTime}
                    encounterDate={encounterDate}
                    dateOfService={dateOfService}
                    onTimeChange={onTimeChange}
                  />
                )}
                {section === 'billing' && (
                  <BillingCodesSection
                    cptCodes={cptCodes}
                    onCptCodesChange={onCptCodesChange}
                    // Encounter-side WorkingClaimLineItem has wider modifier_sources (4 values
                    // post-GDN-1733) than billing-side WorkingBillingClaimLineItemResponse4 (still
                    // narrow at 2). Backend says CPT modifier sources stay narrow at runtime, so
                    // the structural mismatch is benign — narrow at the prop boundary.
                    cptLineItems={
                      cptLineItems as WorkingBillingClaimLineItemResponse4[] | undefined
                    }
                    onCptLineItemsChange={onCptLineItemsChange}
                    suggestedCptCodes={suggestedCptCodes}
                    onUseSuggestedCptCodes={onUseSuggestedCptCodes}
                    showModifiers
                    claimDiagnosisCodes={icd10Codes}
                    icd10Codes={icd10Codes}
                    onIcd10CodesChange={onIcd10CodesChange}
                    psychotherapyMinutes={attestations.psychotherapy_minutes}
                    onPsychotherapyMinutesChange={onAttestationChange}
                    editable={editable}
                  />
                )}
                {section === 'attestations' && (
                  <AttestationsSection
                    modality={modality}
                    attestations={attestations}
                    onAttestationChange={onAttestationChange}
                    hasPatient={hasPatient}
                    editable={editable}
                  />
                )}
              </AccordionDetails>
            </Accordion>
          );
        })}
      </Box>

      {/* Sign/Complete button (note view only) */}
      {showSignButton && onSignNote && (
        <Box
          sx={{
            p: 2,
            borderTop: 1,
            borderColor: 'divider',
            flexShrink: 0,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Button
              variant="contained"
              onClick={() => {
                encounterTracking.billingSignNoteClicked({ has_patient: hasPatient, has_note_signing: hasNoteSigning });
                onClose();
                onSignNote?.();
              }}
              disabled={isSigning}
              startIcon={isSigning ? undefined : <CheckIcon />}
              sx={{
                textTransform: 'none',
                fontWeight: 500,
                py: 1.5,
                px: 3,
                fontSize: '0.9375rem',
                flex: '0 0 75%',
              }}
            >
              {isSigning
                ? 'Processing...'
                : hasNoteSigning && hasPatient
                  ? 'Sign Note'
                  : 'Complete Note'}
            </Button>
            <Typography
              variant="body2"
              sx={{
                fontSize: '0.75rem',
                color: 'text.secondary',
                flex: '0 0 25%',
                lineHeight: 1.4,
              }}
            >
              {needsCosign
                ? 'Your supervisor will be notified to co-sign'
                : 'Note reviewed & accurate'}
            </Typography>
          </Box>
        </Box>
      )}
    </Popover>
  );
};
